import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback
} from 'react';
import { usePersistentState } from '../hooks.persistence';
import { useDistributionData } from 'apiTyped/hooks/distribution.query';
import { useDistributionSynthesisFilters } from 'apiTyped/hooks/distribution.query';
import { useV2DistributionMarkets } from 'apiTyped/hooks/v2.distribution.query';

// --- Types ---

export type DistributionChannel = 'on_trade' | 'off_trade';

export interface BrandOption {
  value: string; // brand name (used as key in API calls)
  label: string;
}

export interface CompetitorOption {
  value: string;
  label: string;
}

export interface MarketOption {
  value: string; // market name
  label: string;
  zone?: string;
}

export type ProductType = '1' | '2'; // '1' = Wine, '2' = Spirit

export type PeriodValue = '3m' | '6m' | '12m';
export type ContentMode = 'premium' | 'full';

export interface AppliedFilter {
  id: string;
  label: string; // e.g. "PRICE $", "SKU ≥ 50"
}

export interface V2FiltersState {
  channel: DistributionChannel;
  brand: string | null; // null = All
  competitors: string[]; // empty = All
  market: string | null; // null = not selected yet (force pick)
  productType: ProductType; // default Wine, hidden toggle for now
  period: PeriodValue;
  contentMode: ContentMode;
  appliedFilters: AppliedFilter[];
}

export interface V2FiltersContextValue {
  // Current state
  filters: V2FiltersState;

  // Available options (from API)
  channelOptions: { value: DistributionChannel; label: string }[];
  brandOptions: BrandOption[];
  competitorOptions: CompetitorOption[];
  marketOptions: MarketOption[];

  // Setters
  setChannel: (channel: DistributionChannel) => void;
  setBrand: (brand: string | null) => void;
  setCompetitors: (competitors: string[]) => void;
  setMarket: (market: string | null) => void;
  setProductType: (productType: ProductType) => void;
  setPeriod: (period: PeriodValue) => void;
  setContentMode: (mode: ContentMode) => void;
  addAppliedFilter: (filter: AppliedFilter) => void;
  removeAppliedFilter: (id: string) => void;

  // Loading state
  isLoading: boolean;
}

// --- Context ---

const V2FiltersContext = createContext<V2FiltersContextValue | null>(null);

export const useV2Filters = (): V2FiltersContextValue => {
  const ctx = useContext(V2FiltersContext);
  if (!ctx) {
    throw new Error('useV2Filters must be used within V2FiltersProvider');
  }
  return ctx;
};

// --- Provider ---

export const V2FiltersProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { isLoadingDistribution, distributionData } = useDistributionData();
  const { isLoadingFilters, filters: synthFilters } =
    useDistributionSynthesisFilters();
  const { marketsByContinent, isLoadingMarkets } = useV2DistributionMarkets();

  // --- Derive options from API data ---
  const distribConfig = distributionData as Record<string, any>;

  const brandOptions = useMemo<BrandOption[]>(() => {
    // Brand-mode clients (`marque_analysis=True`) get brand names here;
    // wine-mode clients get an empty `client_brands` and their products
    // live in `client_wines` instead (see
    // `DistribSynthesisHeaderRoute.get_client_brands/get_client_wines` —
    // exactly one of the two is ever populated). This chip is labelled
    // "Brand/Product" precisely to cover both cases.
    const brands: string[] = synthFilters.clientBrands || [];
    if (brands.length > 0) return brands.map((b) => ({ value: b, label: b }));
    const wines: string[] = synthFilters.clientWines || [];
    return wines.map((w) => ({ value: w, label: w }));
  }, [synthFilters.clientBrands, synthFilters.clientWines]);

  const competitorOptions = useMemo<CompetitorOption[]>(() => {
    // Collect all unique rivals from client_marques_distri
    const marques: any[] = distribConfig?.client_marques_distri || [];
    const rivalsSet = new Set<string>();
    marques.forEach((m: any) => {
      (m.rivals || []).forEach((r: string | null) => {
        if (r) rivalsSet.add(r);
      });
    });
    return Array.from(rivalsSet)
      .sort()
      .map((r) => ({ value: r, label: r }));
  }, [distribConfig?.client_marques_distri]);

  const marketOptions = useMemo<MarketOption[]>(() => {
    const byZone: Record<string, { name: string }[]> = marketsByContinent || {};
    const options: MarketOption[] = [];
    Object.entries(byZone).forEach(([zone, markets]) => {
      (markets || []).forEach((m) => {
        options.push({ value: m.name, label: m.name, zone });
      });
    });
    return options;
  }, [marketsByContinent]);

  // Default market: pick the first campaign's market (latest available, like v1)
  const defaultMarket = useMemo<string | null>(() => {
    const campaigns: any[] = distribConfig?.campaigns || [];
    if (campaigns.length > 0) {
      return campaigns[0]?.market?.name || null;
    }
    if (marketOptions.length > 0) {
      return marketOptions[0].value;
    }
    return null;
  }, [distribConfig?.campaigns, marketOptions]);

  // --- Filter state ---
  const [channel, setChannel] = usePersistentState<DistributionChannel>(
    'ws_v2_channel',
    'on_trade'
  );
  const [brand, setBrand] = useState<string | null>(null);
  const [competitors, setCompetitors] = useState<string[]>([]);
  const [market, setMarketState] = useState<string | null>(null);
  const [productType, setProductType] = usePersistentState<ProductType>(
    'ws_v2_product_type',
    '1'
  ); // default Wine
  const [period, setPeriod] = usePersistentState<PeriodValue>(
    'ws_v2_period',
    '6m'
  );
  const [contentMode, setContentMode] = usePersistentState<ContentMode>(
    'ws_v2_content_mode',
    'premium'
  );
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);

  // Resolve market: if user hasn't picked one, use default from API
  const resolvedMarket = market ?? defaultMarket;

  const setMarket = useCallback((m: string | null) => {
    setMarketState(m);
  }, []);

  const addAppliedFilter = useCallback((filter: AppliedFilter) => {
    setAppliedFilters((prev) => {
      if (prev.find((f) => f.id === filter.id)) return prev;
      return [...prev, filter];
    });
  }, []);

  const removeAppliedFilter = useCallback((id: string) => {
    setAppliedFilters((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const channelOptions = useMemo(
    () => [
      { value: 'on_trade' as DistributionChannel, label: 'ON' },
      { value: 'off_trade' as DistributionChannel, label: 'OFF' }
    ],
    []
  );

  const value = useMemo<V2FiltersContextValue>(
    () => ({
      filters: {
        channel,
        brand,
        competitors,
        market: resolvedMarket,
        productType,
        period,
        contentMode,
        appliedFilters
      },
      channelOptions,
      brandOptions,
      competitorOptions,
      marketOptions,
      setChannel,
      setBrand,
      setCompetitors,
      setMarket,
      setProductType,
      setPeriod,
      setContentMode,
      addAppliedFilter,
      removeAppliedFilter,
      isLoading: isLoadingDistribution || isLoadingFilters || isLoadingMarkets
    }),
    [
      channel,
      brand,
      competitors,
      resolvedMarket,
      productType,
      period,
      contentMode,
      appliedFilters,
      channelOptions,
      brandOptions,
      competitorOptions,
      marketOptions,
      setChannel,
      setBrand,
      setCompetitors,
      setMarket,
      setProductType,
      setPeriod,
      setContentMode,
      addAppliedFilter,
      removeAppliedFilter,
      isLoadingDistribution,
      isLoadingFilters,
      isLoadingMarkets
    ]
  );

  return (
    <V2FiltersContext.Provider value={value}>
      {children}
    </V2FiltersContext.Provider>
  );
};
